import { useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  useGetKPIs,
  useGetRecentInspections,
  useGetInspectionTrends,
  useGetDefectDistribution,
  useGetSeverityDistribution,
  useGetTodayStats,
  getDefectTypeLabel,
  getSeverityLabel,
  getSeverityBadgeClass,
} from '../hooks/useQueries';
import KPICard from '../components/KPICard';
import RecentInspectionsTable from '../components/RecentInspectionsTable';
import { useAuth } from '../hooks/useAuth';
import { Link } from '@tanstack/react-router';
import {
  Activity, AlertTriangle, CheckCircle, Target, RefreshCw,
  Camera, Zap, Clock, Shield, HardDrive, Cpu,
  TrendingUp, PieChart as PieIcon, Users, FileText, BarChart3,
} from 'lucide-react';

const DEFECT_COLORS: Record<string, string> = {
  scratch: '#3b82f6',
  crack: '#ef4444',
  warp: '#f59e0b',
  discoloration: '#8b5cf6',
};

const SEVERITY_COLORS: Record<string, string> = {
  good: '#22c55e',
  minorDefect: '#f59e0b',
  majorDefect: '#ef4444',
};

function formatTrendDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded px-3 py-2 text-xs shadow-card-dark">
        <p className="text-muted-foreground mb-1">{label}</p>
        {payload.map((p: any, i: number) => (
          <p key={i} style={{ color: p.color }} className="font-semibold">
            {p.name}: {p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ═══════════════════════════════════════════════════════════════
// OPERATOR DASHBOARD — focused on doing inspections quickly
// ═══════════════════════════════════════════════════════════════
function OperatorDashboard() {
  const { data: todayStats, isLoading: todayLoading } = useGetTodayStats();
  const { data: recentInspections = [], isLoading: recentLoading } = useGetRecentInspections(5);

  const lastResult = todayStats?.lastInspection;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground tracking-tight">Operator Dashboard</h2>
          <p className="text-xs text-muted-foreground mt-0.5 font-mono">
            Quick actions & inspection status
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border text-status-success border-status-success/30 bg-status-success/10">
            <Camera className="w-3 h-3" />
            Camera Ready
          </span>
        </div>
      </div>

      {/* Quick Action + Today Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Start Inspection CTA */}
        <Link
          to="/live-inspection"
          className="md:col-span-1 bg-gradient-to-br from-electric-blue/20 to-electric-blue/5 border border-electric-blue/30 rounded-lg p-6 flex flex-col items-center justify-center gap-3 hover:from-electric-blue/30 hover:to-electric-blue/10 transition-all group cursor-pointer"
        >
          <div className="w-14 h-14 rounded-full bg-electric-blue/20 border border-electric-blue/30 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Zap className="w-7 h-7 text-electric-blue" />
          </div>
          <span className="text-sm font-bold text-electric-blue uppercase tracking-wider">Start Inspection</span>
          <span className="text-[10px] text-muted-foreground">Capture & analyze a component</span>
        </Link>

        {/* Today's Stats */}
        <div className="bg-card border border-border rounded-lg p-5 shadow-card-dark">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-electric-blue" />
            <h3 className="text-sm font-semibold text-foreground">Today's Stats</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Inspections</p>
              <p className="text-2xl font-bold text-foreground">
                {todayLoading ? '—' : todayStats?.todayCount ?? 0}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Pass Rate</p>
              <p className={`text-2xl font-bold ${(todayStats?.todayPassRate ?? 100) >= 70 ? 'text-status-success' : 'text-status-warning'}`}>
                {todayLoading ? '—' : `${todayStats?.todayPassRate ?? 100}%`}
              </p>
            </div>
          </div>
        </div>

        {/* Last Inspection Result */}
        <div className="bg-card border border-border rounded-lg p-5 shadow-card-dark">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-electric-blue" />
            <h3 className="text-sm font-semibold text-foreground">Last Result</h3>
          </div>
          {todayLoading ? (
            <div className="flex items-center justify-center h-16">
              <div className="w-5 h-5 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : lastResult ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={`text-xs px-2 py-0.5 rounded ${getSeverityBadgeClass(lastResult.finalClassification)}`}>
                  {getSeverityLabel(lastResult.finalClassification)}
                </span>
                <span className="text-[10px] font-mono text-muted-foreground">{lastResult.componentId}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Score: <strong className="text-foreground">{lastResult.severityScore}/100</strong></span>
                <span>Confidence: <strong className="text-electric-blue">{lastResult.modelConfidence}%</strong></span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-4">No inspections yet</p>
          )}
        </div>
      </div>

      {/* Recent Inspections (compact) */}
      <div className="bg-card border border-border rounded-lg shadow-card-dark">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Recent Inspections</h3>
            <p className="text-xs text-muted-foreground">Your last 5 inspections</p>
          </div>
          <Link
            to="/live-inspection"
            className="text-xs text-electric-blue hover:underline"
          >
            New Inspection →
          </Link>
        </div>
        <RecentInspectionsTable records={recentInspections} loading={recentLoading} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// QC MANAGER DASHBOARD — quality oversight, trends, alerts
// ═══════════════════════════════════════════════════════════════
function QCManagerDashboard() {
  const { data: kpis, isLoading: kpisLoading, refetch: refetchKPIs } = useGetKPIs();
  const { data: recentInspections = [], isLoading: recentLoading } = useGetRecentInspections(20);
  const { data: trends = [], isLoading: trendsLoading } = useGetInspectionTrends();
  const { data: defectDist = [], isLoading: defectLoading } = useGetDefectDistribution();
  const { data: severityDist = [], isLoading: severityLoading } = useGetSeverityDistribution();

  const trendData = useMemo(() => {
    if (!trends.length) return [];
    return trends.map(([ts, count]) => ({
      date: formatTrendDate(ts),
      inspections: Number(count),
    }));
  }, [trends]);

  const defectData = useMemo(() => {
    if (!defectDist.length) return [];
    return defectDist.map(([type, count]) => ({
      name: getDefectTypeLabel(type),
      count: Number(count),
      type: type as string,
    }));
  }, [defectDist]);

  const severityData = useMemo(() => {
    if (!severityDist.length) return [];
    return severityDist.map(([sev, count]) => ({
      name: getSeverityLabel(sev),
      value: Number(count),
      type: sev as string,
    }));
  }, [severityDist]);

  const rejectionRate = kpis?.rejectionRate ?? 0;
  const showAlert = rejectionRate > 30;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground tracking-tight">Quality Control Dashboard</h2>
          <p className="text-xs text-muted-foreground mt-0.5 font-mono">
            Defect trends, quality metrics & alerts
          </p>
        </div>
        <button
          onClick={() => refetchKPIs()}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-electric-blue transition-colors px-3 py-1.5 rounded border border-border hover:border-electric-blue/30"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {/* Rejection Alert */}
      {showAlert && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-status-danger/30 bg-status-danger/10">
          <AlertTriangle className="w-5 h-5 text-status-danger flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-status-danger">High Rejection Rate Alert</p>
            <p className="text-xs text-muted-foreground">
              Rejection rate is at <strong>{rejectionRate}%</strong> — exceeds 30% threshold. Review inspection parameters and production line.
            </p>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Inspections"
          value={kpisLoading ? '—' : String(kpis?.totalInspections ?? 0)}
          subtitle="All time"
          icon={<Activity className="w-5 h-5" />}
          colorClass="text-electric-blue"
          loading={kpisLoading}
        />
        <KPICard
          title="Rejection Rate"
          value={kpisLoading ? '—' : `${rejectionRate}%`}
          subtitle={rejectionRate > 30 ? '⚠ Above threshold' : 'Within target'}
          icon={<AlertTriangle className="w-5 h-5" />}
          colorClass={rejectionRate > 30 ? 'text-status-danger' : 'text-status-warning'}
          loading={kpisLoading}
        />
        <KPICard
          title="Major Defects"
          value={kpisLoading ? '—' : String(kpis?.majorDefectCount ?? 0)}
          subtitle="Critical failures"
          icon={<AlertTriangle className="w-5 h-5" />}
          colorClass="text-status-danger"
          loading={kpisLoading}
        />
        <KPICard
          title="Model Accuracy"
          value={kpisLoading ? '—' : `≥${kpis?.modelAccuracy ?? 90}%`}
          subtitle="AI detection accuracy"
          icon={<Target className="w-5 h-5" />}
          colorClass="text-status-success"
          loading={kpisLoading}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* 7-day Trend */}
        <div className="lg:col-span-2 bg-card border border-border rounded-lg p-5 shadow-card-dark">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Inspection Trend</h3>
              <p className="text-xs text-muted-foreground">Daily inspection volume</p>
            </div>
            <TrendingUp className="w-4 h-4 text-electric-blue opacity-60" />
          </div>
          {trendsLoading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : trendData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
              No trend data yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={trendData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.25 0.008 240)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'oklch(0.55 0.01 240)' }} />
                <YAxis tick={{ fontSize: 10, fill: 'oklch(0.55 0.01 240)' }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="inspections" name="Inspections" stroke="oklch(0.72 0.18 220)" strokeWidth={2} dot={{ fill: 'oklch(0.72 0.18 220)', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Severity Pie */}
        <div className="bg-card border border-border rounded-lg p-5 shadow-card-dark">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Severity Breakdown</h3>
              <p className="text-xs text-muted-foreground">Pass vs defects</p>
            </div>
            <PieIcon className="w-4 h-4 text-electric-blue opacity-60" />
          </div>
          {severityLoading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : severityData.every(d => d.value === 0) ? (
            <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
              No data yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={severityData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} innerRadius={35} strokeWidth={0}>
                  {severityData.map((entry, i) => (
                    <Cell key={i} fill={SEVERITY_COLORS[entry.type] || '#888'} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          )}
          {/* Legend */}
          <div className="flex justify-center gap-4 mt-2">
            {severityData.map((entry, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: SEVERITY_COLORS[entry.type] || '#888' }} />
                <span className="text-[10px] text-muted-foreground">{entry.name} ({entry.value})</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Defect Distribution Bar */}
      <div className="bg-card border border-border rounded-lg p-5 shadow-card-dark">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Defect Distribution</h3>
            <p className="text-xs text-muted-foreground">By defect type</p>
          </div>
          <BarChart3 className="w-4 h-4 text-electric-blue opacity-60" />
        </div>
        {defectLoading ? (
          <div className="h-48 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : defectData.every(d => d.count === 0) ? (
          <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
            No defect data yet
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={defectData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.25 0.008 240)" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'oklch(0.55 0.01 240)' }} />
              <YAxis tick={{ fontSize: 10, fill: 'oklch(0.55 0.01 240)' }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Count" radius={[2, 2, 0, 0]}>
                {defectData.map((entry, i) => (
                  <Cell key={i} fill={DEFECT_COLORS[entry.type] || 'oklch(0.72 0.18 220)'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Recent Inspections (detailed, 20 items) */}
      <div className="bg-card border border-border rounded-lg shadow-card-dark">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Recent Inspections</h3>
            <p className="text-xs text-muted-foreground">Latest 20 records for quality review</p>
          </div>
          <Link to="/reports" className="text-xs text-electric-blue hover:underline">
            View All Reports →
          </Link>
        </div>
        <RecentInspectionsTable records={recentInspections} loading={recentLoading} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ADMIN DASHBOARD — full overview + system health
// ═══════════════════════════════════════════════════════════════
function AdminDashboard() {
  const { data: kpis, isLoading: kpisLoading, refetch: refetchKPIs } = useGetKPIs();
  const { data: recentInspections = [], isLoading: recentLoading } = useGetRecentInspections(15);
  const { data: trends = [], isLoading: trendsLoading } = useGetInspectionTrends();
  const { data: defectDist = [], isLoading: defectLoading } = useGetDefectDistribution();

  const trendData = useMemo(() => {
    if (!trends.length) return [];
    return trends.map(([ts, count]) => ({
      date: formatTrendDate(ts),
      inspections: Number(count),
    }));
  }, [trends]);

  const defectData = useMemo(() => {
    if (!defectDist.length) return [];
    return defectDist.map(([type, count]) => ({
      name: getDefectTypeLabel(type),
      count: Number(count),
      type: type as string,
    }));
  }, [defectDist]);

  const storageUsed = useMemo(() => {
    try {
      let total = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('visioninspect')) {
          total += (localStorage.getItem(key) || '').length * 2; // 2 bytes per char
        }
      }
      return (total / (1024 * 1024)).toFixed(2);
    } catch {
      return '?';
    }
  }, [kpis]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground tracking-tight">Admin Dashboard</h2>
          <p className="text-xs text-muted-foreground mt-0.5 font-mono">
            System overview, metrics & management
          </p>
        </div>
        <button
          onClick={() => refetchKPIs()}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-electric-blue transition-colors px-3 py-1.5 rounded border border-border hover:border-electric-blue/30"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Inspections"
          value={kpisLoading ? '—' : String(kpis?.totalInspections ?? 0)}
          subtitle="All time"
          icon={<Activity className="w-5 h-5" />}
          colorClass="text-electric-blue"
          loading={kpisLoading}
        />
        <KPICard
          title="Rejection Rate"
          value={kpisLoading ? '—' : `${kpis?.rejectionRate ?? 0}%`}
          subtitle="Minor + Major defects"
          icon={<AlertTriangle className="w-5 h-5" />}
          colorClass={Number(kpis?.rejectionRate ?? 0) > 30 ? 'text-status-danger' : 'text-status-warning'}
          loading={kpisLoading}
        />
        <KPICard
          title="Major Defects"
          value={kpisLoading ? '—' : String(kpis?.majorDefectCount ?? 0)}
          subtitle="Critical failures"
          icon={<AlertTriangle className="w-5 h-5" />}
          colorClass="text-status-danger"
          loading={kpisLoading}
        />
        <KPICard
          title="Model Accuracy"
          value={kpisLoading ? '—' : `≥${kpis?.modelAccuracy ?? 90}%`}
          subtitle="AI detection accuracy"
          icon={<Target className="w-5 h-5" />}
          colorClass="text-status-success"
          loading={kpisLoading}
        />
      </div>

      {/* System Health + Quick Links */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* System Health */}
        <div className="bg-card border border-border rounded-lg p-5 shadow-card-dark">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-electric-blue" />
            <h3 className="text-sm font-semibold text-foreground">System Health</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Cpu className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Pipeline</span>
              </div>
              <span className="text-xs text-status-success font-semibold">v2.0 — Enhanced</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HardDrive className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Storage</span>
              </div>
              <span className="text-xs text-foreground font-semibold">{storageUsed} MB</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Active Roles</span>
              </div>
              <span className="text-xs text-foreground font-semibold">3 configured</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Detection</span>
              </div>
              <span className="text-xs text-foreground font-semibold">Canny + CLAHE + Bilateral</span>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-card border border-border rounded-lg p-5 shadow-card-dark">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-4 h-4 text-electric-blue" />
            <h3 className="text-sm font-semibold text-foreground">Quick Actions</h3>
          </div>
          <div className="space-y-2">
            <Link
              to="/live-inspection"
              className="flex items-center gap-2.5 px-3 py-2.5 rounded border border-border hover:border-electric-blue/30 hover:bg-electric-blue/5 transition-all text-xs"
            >
              <Camera className="w-4 h-4 text-electric-blue" />
              <span className="text-foreground">Live Inspection</span>
            </Link>
            <Link
              to="/reports"
              className="flex items-center gap-2.5 px-3 py-2.5 rounded border border-border hover:border-electric-blue/30 hover:bg-electric-blue/5 transition-all text-xs"
            >
              <FileText className="w-4 h-4 text-electric-blue" />
              <span className="text-foreground">View Reports</span>
            </Link>
            <Link
              to="/analytics"
              className="flex items-center gap-2.5 px-3 py-2.5 rounded border border-border hover:border-electric-blue/30 hover:bg-electric-blue/5 transition-all text-xs"
            >
              <BarChart3 className="w-4 h-4 text-electric-blue" />
              <span className="text-foreground">Analytics</span>
            </Link>
            <Link
              to="/system-status"
              className="flex items-center gap-2.5 px-3 py-2.5 rounded border border-border hover:border-electric-blue/30 hover:bg-electric-blue/5 transition-all text-xs"
            >
              <Activity className="w-4 h-4 text-electric-blue" />
              <span className="text-foreground">System Status</span>
            </Link>
          </div>
        </div>

        {/* Users / Roles Card */}
        <div className="bg-card border border-border rounded-lg p-5 shadow-card-dark">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-electric-blue" />
            <h3 className="text-sm font-semibold text-foreground">User Roles</h3>
          </div>
          <div className="space-y-3">
            {[
              { role: 'Operator', access: 'Dashboard, Live Inspection', color: 'text-electric-blue' },
              { role: 'QC Manager', access: 'Dashboard, Reports, Analytics', color: 'text-status-warning' },
              { role: 'Admin', access: 'Full access to all modules', color: 'text-status-success' },
            ].map((item) => (
              <div key={item.role} className="flex items-start gap-2.5">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${item.color.replace('text-', 'bg-')}`} />
                <div>
                  <p className={`text-xs font-semibold ${item.color}`}>{item.role}</p>
                  <p className="text-[10px] text-muted-foreground">{item.access}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Inspection Trend */}
        <div className="bg-card border border-border rounded-lg p-5 shadow-card-dark">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Inspection Trend</h3>
              <p className="text-xs text-muted-foreground">Daily volume</p>
            </div>
            <TrendingUp className="w-4 h-4 text-electric-blue opacity-60" />
          </div>
          {trendsLoading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : trendData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={trendData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.25 0.008 240)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'oklch(0.55 0.01 240)' }} />
                <YAxis tick={{ fontSize: 10, fill: 'oklch(0.55 0.01 240)' }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="inspections" name="Inspections" stroke="oklch(0.72 0.18 220)" strokeWidth={2} dot={{ fill: 'oklch(0.72 0.18 220)', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Defect Distribution */}
        <div className="bg-card border border-border rounded-lg p-5 shadow-card-dark">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Defect Distribution</h3>
              <p className="text-xs text-muted-foreground">By type</p>
            </div>
            <CheckCircle className="w-4 h-4 text-electric-blue opacity-60" />
          </div>
          {defectLoading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : defectData.every(d => d.count === 0) ? (
            <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={defectData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.25 0.008 240)" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'oklch(0.55 0.01 240)' }} />
                <YAxis tick={{ fontSize: 10, fill: 'oklch(0.55 0.01 240)' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Count" radius={[2, 2, 0, 0]}>
                  {defectData.map((entry, i) => (
                    <Cell key={i} fill={DEFECT_COLORS[entry.type] || 'oklch(0.72 0.18 220)'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Recent Inspections (with access to all) */}
      <div className="bg-card border border-border rounded-lg shadow-card-dark">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Recent Inspections</h3>
            <p className="text-xs text-muted-foreground">Latest 15 records</p>
          </div>
          <Link to="/reports" className="text-xs text-electric-blue hover:underline">
            Full Reports →
          </Link>
        </div>
        <RecentInspectionsTable records={recentInspections} loading={recentLoading} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN DASHBOARD — routes to the correct sub-view by role
// ═══════════════════════════════════════════════════════════════
export default function Dashboard() {
  const { user } = useAuth();
  const role = user?.role || 'Operator';

  if (role === 'Quality Control Manager') {
    return <QCManagerDashboard />;
  }
  if (role === 'Admin') {
    return <AdminDashboard />;
  }
  // Default: Operator
  return <OperatorDashboard />;
}
